import * as XLSX from 'xlsx';
import { US_STATES } from './state-mapping.helper';

export interface ParsedLeadRow {
  rowNumber: number;
  businessName: string;
  phoneNumber: string;
  email: string;
  streetAddress: string; // Will be parsed into streetNumber + streetName
  unit: string;
  city: string;
  state: string;
  zip: string;
  jobType: string;
  notes: string;

  // Transformed fields (filled during validation/parsing)
  parsedAddress?: {
    streetNumber: string;
    streetName: string;
  } | null;

  // Validation
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export type ValidationWarning = ValidationError;

export function parseCsv(raw: string): ParsedLeadRow[] {
  // Split by newlines
  const lines = raw.trim().split('\n').map(line => line.replace('\r', ''));

  // Parse each line respecting quoted fields
  const parsedLines = lines.map(line => parseCsvLine(line));

  const [headers, ...rows] = parsedLines;

  // Normalize headers to lowercase
  const normalizedHeaders = headers.map(h => h.toLowerCase().trim());

  // Map to ParsedLeadRow objects
  return rows
    .filter(row => row.some(cell => cell && cell.trim() !== '')) // Skip empty rows
    .map((row, index) => {
      const obj: any = { rowNumber: index + 2, errors: [], warnings: [] }; // +2 for header row and 1-based

      normalizedHeaders.forEach((header, i) => {
        const field = mapCsvHeaderToField(header);
        obj[field] = row[i]?.trim() || '';
      });

      return obj as ParsedLeadRow;
    });
}

export function parseXlsx(raw: string): ParsedLeadRow[] {
  const wb: XLSX.WorkBook = XLSX.read(raw, { type: 'binary' });
  const ws: XLSX.WorkSheet = wb.Sheets[wb.SheetNames[0]];

  const jsonData: any[] = XLSX.utils.sheet_to_json(ws);

  return jsonData.map((row: any, index) => {
    const parsed: ParsedLeadRow = {
      rowNumber: index + 2,
      businessName: row['Business Name'] || '',
      phoneNumber: row['Phone Number'] || '',
      email: row['Email'] || '',
      streetAddress: row['Street Address'] || '',
      unit: row['Unit'] || '',
      city: row['City'] || '',
      state: row['State'] || '',
      zip: row['ZIP'] || '',
      jobType: row['Job Type'] || '',
      notes: row['Notes'] || '',
      errors: [],
      warnings: []
    };
    return parsed;
  });
}

function mapCsvHeaderToField(header: string): string {
  const mapping: Record<string, string> = {
    'business name': 'businessName',
    'phone number': 'phoneNumber',
    'email': 'email',
    'street address': 'streetAddress',
    'unit': 'unit',
    'city': 'city',
    'state': 'state',
    'zip': 'zip',
    'job type': 'jobType',
    'notes': 'notes'
  };

  return mapping[header] || header;
}

function parseCsvLine(line: string): string[] {
  // Parse a CSV line respecting quoted fields that may contain commas
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote ("") -> add one quote to current field
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  // Add last field
  result.push(current.trim());

  return result;
}
